import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateIST } from "@/lib/formatUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, FileEdit, FileCheck, FileWarning, Milk } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import OtherValueDialog from "@/components/shared/OtherValueDialog";
import PartyAutocomplete from "@/components/shared/PartyAutocomplete";
const TABS = {
  consumables: { api: "/mod3/consumables", label: "Consumables" },
  rawmaterial: { api: "/mod3/rawmaterial", label: "Raw Material" },
  rejected: { api: "/mod3/rejected", label: "Rejected / Returns" },
  milk: { api: "/mod3/milk", label: "Milk / Supplies" },
};

const INCOMING_REPORT_TYPE_MAP = {
  consumables: "incoming_consumables",
  rawmaterial: "incoming_rawmaterial",
  rejected: "incoming_rejected",
  milk: "milk_delivery",
};

const INCOMING_COLLECTION_MAP = {
  consumables: "incoming_consumables",
  rawmaterial: "incoming_rawmaterial",
  rejected: "incoming_rejected",
  milk: "milk_delivery",};

const INCOMING_EXPORT_COLUMNS = [{key:"ref_no",label:"Ref"},{key:"party_name",label:"Party"},{key:"material_description",label:"Material"},{key:"quantity",label:"Qty"},{key:"amount_with_gst",label:"Amount With GST"},{key:"amount_without_gst",label:"Amount Without GST"},{key:"invoice_no",label:"Invoice"},{key:"status",label:"Status"}];

// Pre-computed stable header arrays per tab — avoids per-render allocation from inline `.filter().map()`.
const COMMON_HEADERS = ["Ref No.", "Party Name", "Material", "Qty", "Amt With GST", "Amt Without GST", "Doc Type", "Challan/Invoice No.", "Vehicle No."];
const TAB_HEADERS = {
  consumables: [...COMMON_HEADERS, "Invoice Status", "Date"],
  rawmaterial: [...COMMON_HEADERS, "Invoice Status", "Date"],
  rejected: [...COMMON_HEADERS, "Reason", "Invoice Status", "Date"],
};

export default function IncomingMaterial() {
  const [tab, setTab] = useState("consumables");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({});
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  const cfg = TABS[tab];

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await api.get(cfg.api, { params: { location, search: search || undefined } });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [tab, location, search, cfg.api]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(`/drafts/${tab}`, { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [tab, location]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  const loadMasters = useCallback(async () => {
    try {
      const r = await api.get("/masters/parties");
      setParties(r.data.items);
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post(`/drafts/${tab}`, payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, tab, location]);

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post(`/drafts/${tab}`, payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    if (tab === "milk") {
      if (!form.supplier_name?.trim()) { toast.error("Supplier name is required"); return; }
      if (!form.quantity_kg?.toString().trim()) { toast.error("Quantity is required"); return; }
    } else {
      if (!form.supplier?.trim() && !form.party_name?.trim()) { toast.error("Supplier / Party Name is required"); return; }
      if (!form.material_description?.trim()) { toast.error("Material Description is required"); return; }
      if (!form.quantity?.toString().trim()) { toast.error("Quantity is required"); return; }
    }
    setSaving(true);
    try {
      await api.post(cfg.api, { ...formatFormData(form), location });
      toast.success("Entry created");
      if (currentDraftId) await api.post(`/drafts/${tab}/${currentDraftId}/submit`).catch(() => {});
      setShowForm(false);
      setForm({});
      setCurrentDraftId(null);
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/${tab}/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await api.delete(`/drafts/${tab}/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch (err) { logger.error("Failed to delete draft:", err); }
  };

  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const fetchMasters = () => {
    api.get("/masters/parties").then(r => setParties(r.data.items)).catch((err) => { logger.error("Failed to load masters:", err); });
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    setField(otherField.fieldKey, val);
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null); setOtherValue("");
  };

  const handleMarkInvoice = async () => {
    if (!invoiceDialog || !invoiceForm.invoice_no?.trim()) { toast.error("Invoice No. is required"); return; }
    try {
      const coll = INCOMING_COLLECTION_MAP[tab] || "incoming_consumables";
      await api.put(`/invoice/mark-received/${coll}/${invoiceDialog.id}`, invoiceForm);
      toast.success("Invoice marked as received");
      setInvoiceDialog(null);
      setInvoiceForm({});
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="incoming-material-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Incoming Material</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-3 · Consumables, Raw Material, Rejected, Daily Supplies</p>
        </div>
        <Button onClick={() => { setForm({}); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> {tab === "milk" ? "New Milk Entry" : "New Entry"}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setSearch(""); }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-wrap gap-2">
          <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5 flex-wrap">
            {Object.entries(TABS).map(([k, v]) => (
              <TabsTrigger key={k} value={k} className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid={`tab-${k}`}>
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" />
          </div>
        </div>

        <div className="flex items-center mt-3">
          <ExportButtons
            reportType={INCOMING_REPORT_TYPE_MAP[tab] || "incoming_consumables"}
            title={cfg.label}
            entries={entries}
            columns={INCOMING_EXPORT_COLUMNS}
            location={location}
          />
        </div>

        <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType={tab} />

        {Object.keys(TABS).map(tabKey => (
          <TabsContent key={tabKey} value={tabKey}>
            {tabKey === "milk" ? (
              /* Milk / Daily Supplies table */
              <Card className="mt-3">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-teal-700 hover:bg-teal-700">
                      {["Ref No.", "Date", "Supplier", "Qty (Kg)", "Received By", "Remarks", "Logged By"].map(h => (
                        <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No milk/supply entries</TableCell></TableRow>}
                    {entries.map(e => (
                      <TableRow key={e.id} className="hover:bg-teal-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                        <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                        <TableCell className="text-sm">{e.created_at ? formatDateIST(e.created_at) : "—"}</TableCell>
                        <TableCell className="font-medium">{e.supplier_name || "—"}</TableCell>
                        <TableCell className="text-sm font-bold">{e.quantity_kg || "—"}</TableCell>
                        <TableCell className="text-sm">{e.received_by || "—"}</TableCell>
                        <TableCell className="text-sm">{e.remarks || "—"}</TableCell>
                        <TableCell className="text-xs">{e.created_by || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
              </Card>
            ) : (
            <Card className="mt-3">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                    {(TAB_HEADERS[tabKey] || COMMON_HEADERS).map(h => (
                      <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.length === 0 && <TableRow><TableCell colSpan={tabKey === "rejected" ? 12 : 11} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                  {entries.map(e => (
                    <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                      <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                      <TableCell className="font-medium">{e.party_name || "—"}</TableCell>
                      <TableCell className="text-sm">{e.material_description || "—"}</TableCell>
                      <TableCell className="text-sm">{e.quantity || "—"}{e.uom ? ` ${e.uom}` : ""}</TableCell>
                      <TableCell className="text-sm" data-testid={`amount-with-gst-${e.id}`}>{e.amount_with_gst ? `₹${e.amount_with_gst}` : "—"}</TableCell>
                      <TableCell className="text-sm" data-testid={`amount-without-gst-${e.id}`}>{e.amount_without_gst ? `₹${e.amount_without_gst}` : "—"}</TableCell>
                      <TableCell>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${e.doc_type === "Challan" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {e.doc_type || "Invoice"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{e.invoice_no || e.challan_no || "—"}</TableCell>
                      <TableCell className="text-sm">{e.vehicle_no || "—"}</TableCell>
                      {tabKey === "rejected" && <TableCell className="text-sm">{e.reason_for_rejection || "—"}</TableCell>}
                      <TableCell>
                        {e.doc_type === "Challan" && e.invoice_status === "pending" ? (
                          <Button size="sm" className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700" onClick={(ev) => { ev.stopPropagation(); setInvoiceDialog(e); setInvoiceForm({ invoice_received_date: new Date().toISOString().slice(0, 10) }); }} data-testid={`mark-invoice-${e.id}`}>
                            <FileWarning size={12} className="mr-1" /> Mark Invoice
                          </Button>
                        ) : e.doc_type === "Challan" && e.invoice_status === "received" ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 flex items-center gap-1 w-fit"><FileCheck size={10} /> Received</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
            </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>New {cfg.label} Entry</DialogTitle></DialogHeader>
          {tab === "milk" ? (
            /* Milk / Daily Supplies Form */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Supplier Name *</Label>
                <Input value={form.supplier_name || ""} onChange={e => setField("supplier_name", e.target.value)} className="h-11" placeholder="e.g. Local dairy" data-testid="field-milk-supplier" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity (Kg) *</Label>
                <Input type="number" step="0.5" value={form.quantity_kg || ""} onChange={e => setField("quantity_kg", e.target.value)} className="h-11" placeholder="e.g. 5" data-testid="field-milk-qty" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Received By</Label>
                <Input value={form.received_by || ""} onChange={e => setField("received_by", e.target.value)} className="h-11" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
                <Input value={form.remarks || ""} onChange={e => setField("remarks", e.target.value)} className="h-11" />
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <PartyAutocomplete
                value={form.party_name || ""}
                onChange={(v) => setField("party_name", v)}
                label="Party Name *"
                testId="field-party-autocomplete"
                masterType="parties"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Description *</Label>
              <Input value={form.material_description || ""} onChange={e => setField("material_description", e.target.value)} className="h-11" data-testid="field-material" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity</Label>
              <Input type="number" value={form.quantity || ""} onChange={e => setField("quantity", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">UOM</Label>
              <Input value={form.uom || ""} onChange={e => setField("uom", e.target.value)} className="h-11" placeholder="Kg / Nos / Pcs" />
            </div>
            {tab !== "milk" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Amount With GST (Rs.)</Label>
                  <Input type="number" step="0.01" value={form.amount_with_gst || ""} onChange={e => setField("amount_with_gst", e.target.value)} className="h-11" placeholder="0.00" data-testid="field-amount-with-gst" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Amount Without GST (Rs.)</Label>
                  <Input type="number" step="0.01" value={form.amount_without_gst || ""} onChange={e => setField("amount_without_gst", e.target.value)} className="h-11" placeholder="0.00" data-testid="field-amount-without-gst" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Document Type</Label>
              <Select value={form.doc_type || "Invoice"} onValueChange={v => setField("doc_type", v)}>
                <SelectTrigger className="h-11" data-testid="field-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Invoice">Invoice</SelectItem>
                  <SelectItem value="Challan">Challan (Invoice Pending)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">
                {form.doc_type === "Challan" ? "Challan No." : "Invoice No."}
              </Label>
              <Input value={form.invoice_no || ""} onChange={e => setField("invoice_no", e.target.value)} className="h-11" data-testid="field-invoice-no" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
              <Input value={form.vehicle_no || ""} onChange={e => setField("vehicle_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Received By</Label>
              <Input value={form.received_by || ""} onChange={e => setField("received_by", e.target.value)} className="h-11" />
            </div>
            {tab === "rawmaterial" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Heat No.</Label>
                  <Input value={form.heat_no || ""} onChange={e => setField("heat_no", e.target.value)} className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Coil ID</Label>
                  <Input value={form.coil_id || ""} onChange={e => setField("coil_id", e.target.value)} className="h-11" />
                </div>
              </>
            )}
            {tab === "rejected" && (
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Reason for Rejection</Label>
                <Input value={form.reason_for_rejection || ""} onChange={e => setField("reason_for_rejection", e.target.value)} className="h-11" placeholder="Enter reason for rejection" data-testid="field-reason-rejection" />
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
              <Input value={form.remarks || ""} onChange={e => setField("remarks", e.target.value)} className="h-11" />
            </div>
          </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Other" value dialog */}
      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={() => handleOtherSubmit(otherField)}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title="Incoming Material Details" collection={INCOMING_COLLECTION_MAP[tab] || "incoming_consumables"} onRefresh={fetchEntries} />

      {/* Mark Invoice Received Dialog */}
      <Dialog open={!!invoiceDialog} onOpenChange={(open) => { if (!open) { setInvoiceDialog(null); setInvoiceForm({}); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              <span className="flex items-center gap-2"><FileCheck size={18} className="text-emerald-600" /> Mark Invoice Received</span>
            </DialogTitle>
          </DialogHeader>
          {invoiceDialog && (
            <div className="bg-slate-50 border rounded-lg p-3 space-y-1 text-sm mb-2">
              <div className="flex justify-between"><span className="text-gray-500">Ref:</span><span className="font-mono font-bold">{invoiceDialog.ref_no}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Party:</span><span className="font-medium">{invoiceDialog.party_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Material:</span><span>{invoiceDialog.material_description}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Challan No.:</span><span className="font-medium">{invoiceDialog.invoice_no || "—"}</span></div>
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice No. *</Label>
              <Input value={invoiceForm.invoice_no || ""} onChange={e => setInvoiceForm(p => ({ ...p, invoice_no: e.target.value }))} className="h-11" placeholder="Enter invoice number" data-testid="mark-invoice-no" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice Received Date</Label>
              <Input type="date" value={invoiceForm.invoice_received_date || ""} onChange={e => setInvoiceForm(p => ({ ...p, invoice_received_date: e.target.value }))} className="h-11" data-testid="mark-invoice-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInvoiceDialog(null); setInvoiceForm({}); }}>Cancel</Button>
            <Button onClick={handleMarkInvoice} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-mark-invoice-btn">
              <FileCheck size={14} className="mr-1" /> Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
